import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Network,
  CalendarClock,
  Calendar,
  Settings,
  ShieldCheck,
  Search,
  Bell,
  ChevronDown,
  LogOut,
  User as UserIcon,
  Inbox,
  Megaphone,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { useNotifications, useMarkNotificationRead, useAnnouncementUnreadCount } from '@/lib/queries';
import { useState, useRef, useEffect } from 'react';
import type { Role } from '@isoft-people/types';

type NavItem = {
  to: string;
  label: string;
  icon: any;
  exact?: boolean;
  roles: Role[];
};

const ALL_NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: Home, exact: true, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/colaboradores', label: 'Colaboradores', icon: Users, roles: ['ADMIN', 'HR', 'MANAGER'] },
  { to: '/organigrama', label: 'Organigrama', icon: Network, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/permisos', label: 'Permisos y Vacaciones', icon: CalendarClock, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/bandeja', label: 'Bandeja de aprobaciones', icon: Inbox, roles: ['ADMIN', 'HR', 'MANAGER'] },
  { to: '/calendario', label: 'Calendario', icon: Calendar, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/anuncios', label: 'Anuncios', icon: Megaphone, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/configuracion', label: 'Configuración', icon: Settings, roles: ['ADMIN', 'HR'] },
  { to: '/auditoria', label: 'Auditoría', icon: ShieldCheck, roles: ['ADMIN'] },
];

export function DashboardLayout() {
  const user = useAuthStore((s) => s.user);
  const role: Role = (user?.role ?? 'EMPLOYEE') as Role;
  const navItems = ALL_NAV.filter((n) => n.roles.includes(role));
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { data: notifs } = useNotifications();
  const { data: unreadAnn } = useAnnouncementUnreadCount();
  const markRead = useMarkNotificationRead();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Sidebar */}
      <aside className="flex w-60 flex-shrink-0 flex-col bg-bg-sidebar text-white">
        <div className="flex h-16 items-center gap-3 border-b border-white/5 px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary-light to-primary">
            <div className="h-3 w-3 rounded-full bg-white" />
          </div>
          <span className="text-base font-semibold tracking-wide">ISOFT People</span>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `flex h-10 items-center gap-3 rounded-md px-3 text-sm transition ${
                  isActive
                    ? 'bg-primary font-semibold text-white shadow-sm'
                    : 'font-medium text-white/60 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon size={18} strokeWidth={2} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.to === '/anuncios' && (unreadAnn?.count ?? 0) > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold text-white">
                  {unreadAnn!.count}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 p-4 text-[11px] text-white/40">
          v1.0 · ISOFT © 2026
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-border bg-white px-8">
          <div className="flex h-10 w-[380px] items-center gap-2 rounded-md border border-border bg-bg px-3">
            <Search size={16} className="text-font-tertiary" />
            <input
              placeholder="Buscar colaborador, permiso..."
              className="flex-1 bg-transparent text-sm placeholder:text-font-tertiary focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-5">
            <button className="flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-font-secondary hover:bg-bg">
              <span>GT</span>
              <ChevronDown size={14} />
            </button>

            <div className="relative">
              <button
                onClick={() => setShowNotifs((v) => !v)}
                className="relative flex h-9 w-9 items-center justify-center rounded-md hover:bg-bg"
              >
                <Bell size={18} className="text-font-secondary" />
                {notifs && notifs.unreadCount > 0 && (
                  <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-error text-[10px] font-semibold text-white">
                    {notifs.unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-11 z-50 max-h-[480px] w-96 overflow-y-auto rounded-lg border border-border bg-white shadow-xl">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <h3 className="text-sm font-semibold">Notificaciones</h3>
                    <button
                      onClick={() => {
                        setShowNotifs(false);
                        navigate('/notificaciones');
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Ver todas
                    </button>
                  </div>
                  {notifs?.items.length === 0 && (
                    <p className="p-6 text-center text-xs text-font-secondary">
                      Sin notificaciones
                    </p>
                  )}
                  {notifs?.items.slice(0, 6).map((n: any) => (
                    <button
                      key={n.id}
                      onClick={() => {
                        if (!n.readAt) markRead.mutate(n.id);
                        if (n.link) navigate(n.link);
                        setShowNotifs(false);
                      }}
                      className={`flex w-full items-start gap-3 border-b border-border-light p-3 text-left hover:bg-bg ${
                        !n.readAt ? 'bg-primary-subtle/30' : ''
                      }`}
                    >
                      <div className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-font-primary">{n.title}</p>
                        <p className="mt-0.5 text-xs text-font-secondary line-clamp-2">
                          {n.body}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-xs font-semibold text-white"
              >
                {user ? initials(user.fullName ?? user.email) : 'AD'}
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-11 z-50 w-56 rounded-lg border border-border bg-white p-1 shadow-xl">
                  <div className="border-b border-border px-3 py-2">
                    <p className="text-sm font-semibold text-font-primary">
                      {user?.fullName ?? 'Admin'}
                    </p>
                    <p className="truncate text-xs text-font-secondary">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/mi-perfil');
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-font-primary hover:bg-bg"
                  >
                    <UserIcon size={14} /> Mi perfil
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-error hover:bg-red-50"
                  >
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
